/**
 * Tipos y llamada REAL de `GET /consola/conductores` (`apps/api/src/conductores/
 * consola-conductores.controller.ts`). Mismo criterio que `verificacionApi.ts` / `incidentesApi.
 * ts` / `posicionesApi.ts`: estos tipos NO son los de `dominio/contratos.ts` — son DISTINTOS a
 * propósito, y por la misma causa (el mock se declaró una ronda ANTES de que existiera
 * `apps/api`).
 *
 * 🔑 **ESTE ARCHIVO ES ÚNICO Y LO CONSUMEN DOS PANTALLAS: `Conductores.tsx` y `Suscripciones.
 * tsx`.** No es casualidad ni reuso oportunista: las dos leen EL MISMO endpoint. `Conductores`
 * lo usa entero (expediente, disponibilidad, vehículo, cuenta); `Suscripciones` usa el mismo
 * listado filtrado por `suscripcion !== null`, porque `ConductorListado.suscripcion.estado` es lo
 * único que hay hoy para armar esa vista (ver el bloque «lo que este endpoint NO trae»). Escribir
 * dos clientes del mismo endpoint habría dejado dos definiciones del mismo contrato que se
 * separan en cuanto una de las dos se toque.
 *
 * 🔴 **DIFERENCIAS MEDIDAS contra los mock de `dominio/contratos.ts`** (leídas de
 * `apps/api/prisma/schema.prisma`, no adivinadas):
 *
 * ```
 *                          MOCK (dominio/contratos.ts)      REAL (apps/api)
 * EstadoVerificacion       SIN_ENVIAR·EN_REVISION·          PENDIENTE·EN_REVISION·APROBADO·
 *                          APROBADO·OBSERVADO·RECHAZADO      RECHAZADO·SUSPENDIDO
 * EstadoDisponibilidad     DISPONIBLE·OCUPADO·               DESCONECTADO·DISPONIBLE·OCUPADO
 *                          FUERA_DE_LINEA                    (`FUERA_DE_LINEA` NO EXISTE)
 * EstadoCuenta             ACTIVA·SUSPENDIDA·DE_BAJA         ACTIVA·SUSPENDIDA·DADA_DE_BAJA
 *                                                            (`DE_BAJA` NO EXISTE)
 * EstadoSuscripcion        SIN_INICIAR·PENDIENTE·ACTIVA·     idénticos — es el ÚNICO de los
 *                          FALLIDA·VENCIDA·CANCELADA          cuatro que coincide (verificado
 *                                                             valor por valor, no asumido)
 * nombre / telefono        `string` (siempre presentes)      `string | null` los dos
 * motivoSuspension         existe en el mock                 NO EXISTE en el schema — no hay
 *                                                             columna ni historial de suspensión
 * ```
 *
 * ⇒ tres de los cuatro diccionarios de `componentes/Etiqueta.tsx` están indexados por valores que
 * el backend no produce: pintar `DADA_DE_BAJA` o `DESCONECTADO` con ellos compilaría (todos son
 * `string` bajo el capó) y mostraría el valor CRUDO en mayúsculas. Por eso las etiquetas de estas
 * dos pantallas viven en `componentes/EtiquetaConductor.tsx`, indexadas por los enums de acá.
 *
 * 🔴 **LO QUE ESTE ENDPOINT NO TRAE, y hay que saberlo antes de diseñar una pantalla encima:**
 *  - **De la suscripción, SÓLO `estado`.** El servicio proyecta `{ estado }` y nada más, aunque
 *    el modelo `Suscripcion` tenga `vigenteHasta`, `hubPagosSubId` y `ultimaSyncAt`. Esos tres se
 *    obtienen por conductor puntual: `GET /consola/verificacion/conductores/:id` (→
 *    `verificacionApi.obtenerDetalle`) o la respuesta de `suscripcionApi.reconciliarSuscripcion`.
 *  - **`montoMensual` y `moneda` NO EXISTEN en ningún lado.** No es que el endpoint no los
 *    proyecte: el modelo `Suscripcion` no tiene esas columnas. El importe del plan vive en
 *    hub-pagos, que es la fuente de verdad del cobro (R-09). La columna «Monto» del mock era una
 *    invención del mock.
 *  - **`User.deletedAt` NO se proyecta**, y ese es un hallazgo conocido del backend que NO se
 *    arregla desde acá: un usuario dado de baja (despersonalizado) sigue apareciendo en el
 *    listado. ⚠️ **`estadoCuenta === 'DADA_DE_BAJA'` es OTRO campo** (`Conductor.estadoCuenta`) y
 *    puede no estar sincronizado con `User.deletedAt` ⇒ **la consola no puede distinguir de forma
 *    fiable a un usuario despersonalizado**, y esta pantalla no finge que sí. `nombre: null`
 *    tampoco sirve de discriminador: también es `null` para un conductor que nunca cargó nombre.
 *
 * ⚠️ **Grado de verificación, declarado (§2):** el contrato de abajo está LEÍDO del servicio y del
 * controller reales y cotejado contra `consola-conductores.integration.spec.ts` (que corre el
 * controller con `ValidationPipe` y los guards reales). **No** hubo un `fetch` desde el bundle de
 * la consola contra un backend vivo — mismo límite que ya declara `clienteApi.ts` para los demás
 * endpoints.
 *
 * ⛔ **CORREGIDO 2026-09-05: acá decía que ese límite no se podía levantar «porque `apps/api`
 * todavía no lee la cookie de `auth-jj`». Ya no aplica** — la consola tiene login propio y
 * `apps/api` sirve y lee su cookie (`docs/06` §13). Lo que falta para levantarlo es un navegador
 * contra un backend vivo, que es otra cosa y sigue pendiente.
 */
import { hayBackend } from '../config/entorno';
import { obtener } from './clienteApi';

// ---------------------------------------------------------------------------
// Enums reales — leídos de apps/api/prisma/schema.prisma.
// ---------------------------------------------------------------------------

export type EstadoVerificacionReal =
  'PENDIENTE' | 'EN_REVISION' | 'APROBADO' | 'RECHAZADO' | 'SUSPENDIDO';

export type EstadoDisponibilidadReal = 'DESCONECTADO' | 'DISPONIBLE' | 'OCUPADO';

export type EstadoCuentaReal = 'ACTIVA' | 'SUSPENDIDA' | 'DADA_DE_BAJA';

export type EstadoSuscripcionReal =
  'SIN_INICIAR' | 'PENDIENTE' | 'ACTIVA' | 'FALLIDA' | 'VENCIDA' | 'CANCELADA';

/**
 * 📌 `MOTOTAXI` RETIRADO el 2026-09-01 (decisión del owner: el servicio es sólo vehículos
 * taxi — autos y/o camionetas). Coincide con `enum TipoVehiculo { AUTO }` de
 * `apps/api/prisma/schema.prisma`, que es de donde sale este tipo.
 */
export type TipoVehiculoReal = 'AUTO';

export const ESTADOS_CUENTA: EstadoCuentaReal[] = ['ACTIVA', 'SUSPENDIDA', 'DADA_DE_BAJA'];

export const ESTADOS_DISPONIBILIDAD: EstadoDisponibilidadReal[] = [
  'DESCONECTADO',
  'DISPONIBLE',
  'OCUPADO',
];

export const ESTADOS_VERIFICACION_CONDUCTOR: EstadoVerificacionReal[] = [
  'PENDIENTE',
  'EN_REVISION',
  'APROBADO',
  'RECHAZADO',
  'SUSPENDIDO',
];

// ---------------------------------------------------------------------------
// GET /consola/conductores
// ---------------------------------------------------------------------------

/**
 * Espejo exacto de `ConductorListado` (`consola-conductores.service.ts`).
 *
 * ⚠️ **`reputacion` es STRING, no number** — `Prisma.Decimal` serializa a texto sobre HTTP
 * (`"4.80"`, no `4.8`), la misma trampa que ya documenta `verificacionApi.ConductorDetalle`. El
 * servicio la declara `unknown` justamente para no mentir sobre el tipo; acá se declara lo que
 * viaja por el cable. Cualquier aritmética necesita `Number()` explícito.
 *
 * ⚠️ **`vehiculo.tipo` llega como `string`** en el DTO del servicio, aunque el único valor que
 * Prisma puede producir sea `AUTO` (desde que se retiró `MOTOTAXI` el 2026-09-01). Se estrecha
 * acá al enum real, pero toda lectura tiene que degradar bien ante un valor desconocido — y eso
 * **ahora importa MÁS, no menos**: una base que todavía no aplicó la migración puede seguir
 * devolviendo `MOTOTAXI` por el cable. Por eso las etiquetas usan una búsqueda con fallback y
 * no un índice directo.
 */
export interface ConductorListado {
  id: string;
  /** `null` si el usuario nunca cargó nombre, o si la cuenta se despersonalizó por baja. */
  nombre: string | null;
  telefono: string | null;
  estadoVerificacion: EstadoVerificacionReal;
  estadoDisponibilidad: EstadoDisponibilidadReal;
  estadoCuenta: EstadoCuentaReal;
  /** Decimal → STRING sobre HTTP. `null` si nunca recibió calificaciones. */
  reputacion: string | null;
  viajesTotales: number;
  ultimoViajeFinAt: string | null;
  vehiculo: { placa: string; tipo: TipoVehiculoReal } | null;
  /** Sólo el estado. El resto de la suscripción NO viaja acá — ver la cabecera del archivo. */
  suscripcion: { estado: EstadoSuscripcionReal } | null;
}

/**
 * 🔴 **TOPE DEL BACKEND, ESPEJADO A PROPÓSITO.** `ConsolaConductoresService` corta con
 * `take: 200` y **no devuelve ninguna señal de que cortó**: 200 filas y 200-de-5000 se ven
 * exactamente igual en la respuesta (mismo status, mismo cuerpo bien formado). La única señal
 * disponible del lado de la consola es la CANTIDAD, y por eso este número tiene que estar acá.
 *
 * ⚠️ **Si el backend cambia su `TOPE_FILAS`, esta constante queda stale y el aviso de la pantalla
 * deja de aparecer justo cuando más falta hace.** Es un acoplamiento conocido y sin detector: no
 * hay hoy un endpoint que publique el tope. La alternativa —no avisar— es peor: un listado
 * truncado en silencio se lee como la flota entera.
 */
export const TOPE_FILAS_CONDUCTORES = 200;

export interface FiltroConductores {
  estadoCuenta?: EstadoCuentaReal;
  estadoDisponibilidad?: EstadoDisponibilidadReal;
  estadoVerificacion?: EstadoVerificacionReal;
}

/**
 * Los tres filtros se mandan AL BACKEND, no se aplican client-side.
 *
 * 🔑 No es una preferencia de estilo: con `take: 200`, filtrar en el cliente filtra **dentro de
 * las 200 primeras filas por `updatedAt`**, así que «suspendidos» podría dar vacío teniendo la
 * base llena de suspendidos si ninguno entró en esa ventana. Mandando el filtro, el tope se
 * aplica al conjunto YA filtrado y el resultado es el correcto hasta 200 coincidencias.
 *
 * ⚠️ El backend NO tiene búsqueda de texto (`?q=`) ni paginación: eso sigue siendo client-side,
 * con el límite que el aviso de tope explica en pantalla.
 */
export const obtenerConductores = (filtro: FiltroConductores = {}): Promise<ConductorListado[]> => {
  if (!hayBackend) {
    // Sin `VITE_API_URL`, `pedir()` armaría una URL relativa y el fallo llegaría como un 404 o un
    // error de red que no le dice nada a quien lo lee. Este mensaje sí.
    return Promise.reject(
      new Error(
        'No hay backend configurado (falta VITE_API_URL): esta pantalla no tiene a quién pedirle los conductores.',
      ),
    );
  }
  const parametros = new URLSearchParams();
  if (filtro.estadoCuenta) parametros.set('estadoCuenta', filtro.estadoCuenta);
  if (filtro.estadoDisponibilidad)
    parametros.set('estadoDisponibilidad', filtro.estadoDisponibilidad);
  if (filtro.estadoVerificacion) parametros.set('estadoVerificacion', filtro.estadoVerificacion);
  const cadena = parametros.toString();
  return obtener<ConductorListado[]>(`/consola/conductores${cadena ? `?${cadena}` : ''}`);
};
